import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import {
  Home,
  Users,
  Building2,
  CreditCard,
  BarChart2,
  ListChecks,
  FileText,
  Settings,
  User,
  LayoutDashboard,
  Bell
} from 'lucide-react';
import NotificationDrawer from './NotificationDrawer';
import { AuthContext } from '../context/AuthContext';
import { logout as apiLogout } from '../api/auth';
import axiosInstance from '../api/axiosInstance';

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  const navigate = useNavigate();
  const { clearSession } = useContext(AuthContext);

  useEffect(() => {
    let mounted = true;

    async function fetchUnreadState() {
      try {
        const res = await axiosInstance.get('/api/dashboard/notifications', {
          withCredentials: true,
        });

        if (!mounted) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setHasUnread(list.some((notification) => !notification.read));
      } catch (err) {
        console.error('Failed to fetch notification state:', err);
        if (mounted) setHasUnread(false);
      }
    }

    fetchUnreadState();

    return () => {
      mounted = false;
    };
  }, []);

  const handleOpenNotifications = () => {
    setShowNotifications(true);
    setHasUnread(false);
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.error('Logout failed (backend)', e);
    }
    clearSession?.();
    navigate('/sign-in');
  };

  return (
    <>
      {/* Top Bar with Logo and Hamburger (Mobile Only) */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-4 py-3 bg-[#000000]/60 backdrop-blur-md border-b border-white/10">
        <Link to="/dashboard">
          <img
            src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
            alt="Beem Logo"
            className="w-24"
          />
        </Link>

        {/* Hamburger Button */}
        <button onClick={() => setOpen(!open)} aria-label="Open navigation">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Sidebar (Mobile + Desktop) */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-64 bg-[#000000]/50 backdrop-blur-lg p-6 flex flex-col custom-scrollbar overflow-y-auto z-40
          transition-transform duration-200
          ${open ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static md:block
        `}
      >
        {/* Close button for mobile */}
        <button
          className="md:hidden mb-4 self-end text-white"
          onClick={() => setOpen(false)}
          aria-label="Close navigation"
        >
          ✕
        </button>

        {/* Logo (Desktop view) */}
        <div className="mb-10 hidden md:block">
          <Link to="/dashboard">
            <img
              src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
              alt="Beem Logo"
              className="w-28 ml-3"
            />
          </Link>
        </div>

        {/* Nav Links */}
        <nav className="flex flex-col space-y-4">
          <Link
            onClick={() => setOpen(false)}
            to="/dashboard"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>

          <button
            onClick={handleOpenNotifications}
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition text-left w-full relative"
          >
            <div className="relative">
              <Bell className="w-5 h-5" />
              {hasUnread && (
                <span className="absolute top-0 right-0 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              )}
            </div>
            <span>Notifications</span>
          </button>

          <Link
            onClick={() => setOpen(false)}
            to="/properties"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <Home className="w-5 h-5" />
            <span>Properties</span>
          </Link>

          <Link
            onClick={() => setOpen(false)}
            to="/contacts"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <Users className="w-5 h-5" />
            <span>Contacts</span>
          </Link>

          <Link
            to="/payments"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <CreditCard className="w-5 h-5" />
            <span>Payments</span>
          </Link>

          <Link
            to="/analytics"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <BarChart2 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>

          <Link
            onClick={() => setOpen(false)}
            to="/tasks"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <ListChecks className="w-5 h-5" />
            <span>Tasks & Jobs</span>
          </Link>

          <Link
            onClick={() => setOpen(false)}
            to="/documents"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <FileText className="w-5 h-5" />
            <span>Documents</span>
          </Link>

          <Link
            onClick={() => setOpen(false)}
            to="/teams"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <Building2 className="w-5 h-5" />
            <span>Teams</span>
          </Link>

          <Link
            onClick={() => setOpen(false)}
            to="/settings"
            className="flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition"
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <hr className="my-6 border-t border-white/25" />

        <div className="mt-auto">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-md hover:bg-[#677181] transition text-left"
          >
            <User className="w-5 h-5" />
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-30 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Notification Drawer */}
      {showNotifications && <NotificationDrawer onClose={() => setShowNotifications(false)} />}
    </>
  );
}
